"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSelection = void 0;
const react_redux_1 = require("react-redux");
const use_meta_1 = require("./use_meta");
const selector_1 = require("../store/selector");
const core_1 = require("../core");
const getSelectedFieldIds = (state, currentDatasheetId) => {
    if (!(0, selector_1.isCurrentDatasheetActive)(state, currentDatasheetId)) {
        return;
    }
    // TODO: to read directly from the Range.
    const matrix = core_1.Selectors.getCellMatrixFromSelection(state);
    if (!matrix) {
        return;
    }
    const fieldIds = [];
    const recordIds = [];
    const columnsCell = matrix[0] || [];
    matrix.forEach(row => {
        if (!row[0]) {
            return;
        }
        recordIds.push(row[0].recordId);
    });
    columnsCell.forEach(col => {
        fieldIds.push(col.fieldId);
    });
    return {
        recordIds,
        fieldIds,
    };
};
/**
 * Get the recordId and fieldId of the region selected by the current cell cursor.
 * Rerendering is triggered when the cursor is moved or the view is switched.
 *
 * If you only need information about the active cell, please use {@link useActiveCell}.
 *
 * @returns
 *
 * ### Example
 * ```js
 * import { useSelection, useRecords, useFields, useActiveViewId } from '@apitable/widget-sdk';
 *
 * // Render the currently selection information
 * function Selection() {
 *   const selection = useSelection();
 *   const viewId = useActiveViewId();
 *   const records = useRecords(viewId, { ids: selection?.recordIds });
 *   const fields = useFields(viewId, { ids: selection?.fieldIds });
 *
 *   return (<table>
 *     <thead>
 *       <tr>
 *         {fields.map(field => <th key={field.id}>{field.name || '_'}</th>)}
 *       </tr>
 *     </thead>
 *     <tbody>
 *       {records.map(record =>
 *         <tr key={record.id}>
 *           {fields.map(field =>
 *             <td key={field.id}>{record.getCellValueString(field.id) || '_'}</td>
 *           )}
 *        </tr>
 *       )}
 *     </tbody>
 *   </table>);
 * }
 * ```
 *
 */
function useSelection() {
    const { datasheetId } = (0, use_meta_1.useMeta)();
    return (0, react_redux_1.useSelector)(state => {
        return getSelectedFieldIds(state, datasheetId);
    }, react_redux_1.shallowEqual);
}
exports.useSelection = useSelection;
